import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { fetchPageAssets } from '../../ducks/hostedAssets.duck';
import { addMarketplaceEntities, getListingsById } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { getImageVariantInfo } from '../EditListingPage/EditListingPage.duck';

export const ASSET_NAME = 'landing-page';
export const ASSET_NAME_DUTCH = 'nl-home';
//

// (Re)defined constants & helpers that were implicit in legacy code
const PER_PAGE = 24; // Fallback page size; adjust if needed
const COLLECTIBLES_PER_PAGE = 4;

// Helper to transform response to listing id array
const responseListingIds = data => data.data.map(l => l.id);

// Build default search params for listings
const getDefaultParams = config => {
  const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);

  return {
    include: ['author', 'images'],
    // 'fields.listing': [
    //   'title',
    //   'geolocation',
    //   'price',
    //   'deleted',
    //   'state',
    //   'publicData.listingType',
    //   'publicData.transactionProcessAlias',
    //   'publicData.unitType',
    //   'publicData.cardStyle',
    //   'publicData.pickupEnabled',
    //   'publicData.shippingEnabled',
    //   'publicData.priceVariationsEnabled',
    //   'publicData.priceVariants',
    // ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': imageVariantInfo.fieldsImage,
    ...imageVariantInfo.imageVariants,
    'limit.images': 1,
    perPage: PER_PAGE,
    pub_status: 'open',
  };
};

// ================ Async thunks ================ //

export const searchFeaturedListings = createAsyncThunk(
  'landingPage/searchFeaturedListings',
  async (config, { dispatch, rejectWithValue, extra: sdk }) => {
    try {
      const response = await sdk.listings.query({
        ...getDefaultParams(config),
        pub_featured: true,
        minStock:1
      });
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      return responseListingIds(response.data);
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  },
  { serializeError: storableError }
);

export const searchRecentListings = createAsyncThunk(
  'landingPage/searchRecentListings',
  async (arg, { dispatch, rejectWithValue, extra: sdk }) => {
    try {
      const cfg = arg?.config || arg;
      const excludeIdRaw = arg?.excludeId;
      const excludeId =
        typeof excludeIdRaw === 'string'
          ? excludeIdRaw
          : excludeIdRaw?.uuid || excludeIdRaw?.id?.uuid || null;

      const response = await sdk.listings.query({
        ...getDefaultParams(cfg),
        minStock:1
      });
      const listingFields = cfg?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      let responseForStore = response;
      let dataForIds = response.data;

      if (excludeId) {
        const allListings = response?.data?.data || [];
        const filteredListings = allListings.filter(l => l?.id?.uuid !== excludeId);

        const allowedImageIds = new Set();
        filteredListings.forEach(l => {
          const relImgs = l?.relationships?.images?.data || [];
          relImgs.forEach(ref => {
            const imgUuid = ref?.id?.uuid;
            if (imgUuid) allowedImageIds.add(imgUuid);
          });
        });

        const included = response?.data?.included || [];
        const filteredIncluded = included.filter(inc => {
          return inc?.type === 'image' ? allowedImageIds.has(inc?.id?.uuid) : true;
        });

        responseForStore = {
          ...response,
          data: {
            ...response.data,
            data: filteredListings,
            included: filteredIncluded,
          },
        };
        dataForIds = responseForStore.data;
      }

      dispatch(addMarketplaceEntities(responseForStore, sanitizeConfig));
      return responseListingIds(dataForIds);
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  },
  { serializeError: storableError }
);

export const searchCollectiblesListings = createAsyncThunk(
  'landingPage/searchCollectiblesListings',
  async (config, { dispatch, rejectWithValue, extra: sdk }) => {
    try {
      const response = await sdk.listings.query({
        include: ['images'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
        'limit.images': 1,
        perPage: COLLECTIBLES_PER_PAGE,
        pub_categoryLevel1: 'collectibles-and-accessories',
      });
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      return responseListingIds(response.data);
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  },
  { serializeError: storableError }
);

// ================ Page asset loader ================ //
export const loadData = (params, search, config) => async (dispatch, getState, { sdk }) => {
  const pageAsset = { [ASSET_NAME_DUTCH]: `content/pages/${ASSET_NAME_DUTCH}.json` };
  dispatch(searchFeaturedListings(config));
  dispatch(searchRecentListings(config));
  return Promise.all([
    dispatch(searchCollectiblesListings(config)),
    dispatch(fetchPageAssets(pageAsset, true)),
  ]);
};

// ================ Slice ================ //

const initialState = {
  featuredListingIds: [],
  featuredListingsInProgress: false,
  featuredListingsError: null,
  recentListingIds: [],
  recentListingsInProgress: false,
  recentListingsError: null,
  collectiblesListingIds: [],
  collectiblesListingsInProgress: false,
  collectiblesListingsError: null,
};

const landingPageSlice = createSlice({
  name: 'landingPage',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      // Featured listings
      .addCase(searchFeaturedListings.pending, state => {
        state.featuredListingsInProgress = true;
        state.featuredListingsError = null;
      })
      .addCase(searchFeaturedListings.fulfilled, (state, action) => {
        state.featuredListingsInProgress = false;
        state.featuredListingIds = action.payload;
      })
      .addCase(searchFeaturedListings.rejected, (state, action) => {
        state.featuredListingsInProgress = false;
        state.featuredListingsError = action.payload;
      })
      .addCase(searchRecentListings.pending, state => {
        state.recentListingsInProgress = true;
        state.recentListingsError = null;
      })
      .addCase(searchRecentListings.fulfilled, (state, action) => {
        state.recentListingsInProgress = false;
        state.recentListingIds = action.payload;
      })
      .addCase(searchRecentListings.rejected, (state, action) => {
        state.recentListingsInProgress = false;
        state.recentListingsError = action.payload;
      })
      .addCase(searchCollectiblesListings.pending, state => {
        state.collectiblesListingsInProgress = true;
        state.collectiblesListingsError = null;
      })
      .addCase(searchCollectiblesListings.fulfilled, (state, action) => {
        state.collectiblesListingsInProgress = false;
        state.collectiblesListingIds = action.payload;
      })
      .addCase(searchCollectiblesListings.rejected, (state, action) => {
        state.collectiblesListingsInProgress = false;
        state.collectiblesListingsError = action.payload;
      });
  },
});

export default landingPageSlice.reducer;

// Selectors for listing IDs
const getFeaturedListingIds = state => state.LandingPage.featuredListingIds || [];
const getRecentListingIds = state => state.LandingPage.recentListingIds || [];
const getCollectiblesListingIds = state => state.LandingPage.collectiblesListingIds || [];

// Memoized selector for featured listings
export const featuredListingsSelector = createSelector(
  [state => state, getFeaturedListingIds],
  (state, listingIds) => getListingsById(state, listingIds) || []
);

// Memoized selector for recent listings
export const recentListingsSelector = createSelector(
  [state => state, getRecentListingIds],
  (state, listingIds) => getListingsById(state, listingIds) || []
);

export const collectiblesListingsSelector = createSelector(
  [state => state, getCollectiblesListingIds],
  (state, listingIds) => getListingsById(state, listingIds) || []
);

export const collectiblesListingsInProgressSelector = state =>
  state.LandingPage.collectiblesListingsInProgress || false;

export const collectiblesListingsErrorSelector = state =>
  state.LandingPage.collectiblesListingsError || null;

// Custom listings selector - uses a factory pattern for memoization with parameters
// This creates a cache keyed by the ID to avoid re-computation
const selectorCache = {};

export const customListingsSelector = (state, id) => {
  // Create a memoized selector for this specific ID if it doesn't exist
  if (!selectorCache[id]) {
    selectorCache[id] = createSelector(
      [
        state => state,
        state => id === 'featured-listings'
          ? state.LandingPage.featuredListingIds
          : state.LandingPage.recentListingIds
      ],
      (state, listingIds) => getListingsById(state, listingIds || []) || []
    );
  }
  return selectorCache[id](state);
};

// Generic custom in-progress selector
export const customInProgressSelector = (state, id) =>
  state.LandingPage[
  id === 'featured-listings' ? 'featuredListingsInProgress' : 'recentListingsInProgress'
  ] || false;
